import { audio } from '../audio/AudioManager.js';
import { setReducedMotionMode, syncMotionAttribute, type ReducedMotionMode } from './anim.js';
import { createSheet, el, type Sheet } from './dom.js';

/**
 * Preferences du joueur : lues au demarrage, appliquees immediatement,
 * persistees dans localStorage. Rien de sensible n'y est stocke.
 */

export type UiScale = 'normal' | 'large';

export interface Settings {
  readonly musicVolume: number;
  readonly sfxVolume: number;
  readonly muted: boolean;
  readonly motion: ReducedMotionMode;
  readonly uiScale: UiScale;
  /** Anti-addiction : rappel de pause. Desactivable, jamais masque. */
  readonly sessionReminder: boolean;
}

const STORAGE_KEY = 'salon-minuit.settings.v1';

export const DEFAULT_SETTINGS: Settings = {
  musicVolume: 0.35,
  sfxVolume: 0.7,
  muted: false,
  // `auto` : on respecte `prefers-reduced-motion` tant que le joueur ne tranche pas.
  motion: 'auto',
  uiScale: 'normal',
  sessionReminder: true,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readVolume(source: Record<string, unknown>, key: string, fallback: number): number {
  const value = source[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(1, Math.max(0, value));
}

function readMotion(value: unknown): ReducedMotionMode {
  return value === 'on' || value === 'off' || value === 'auto' ? value : DEFAULT_SETTINGS.motion;
}

function load(): Settings {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Stockage indisponible (mode prive strict) : on reste sur les defauts.
    return DEFAULT_SETTINGS;
  }
  if (raw === null) return DEFAULT_SETTINGS;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return DEFAULT_SETTINGS;
  }
  if (!isRecord(parsed)) return DEFAULT_SETTINGS;

  return {
    musicVolume: readVolume(parsed, 'musicVolume', DEFAULT_SETTINGS.musicVolume),
    sfxVolume: readVolume(parsed, 'sfxVolume', DEFAULT_SETTINGS.sfxVolume),
    muted: typeof parsed['muted'] === 'boolean' ? parsed['muted'] : DEFAULT_SETTINGS.muted,
    motion: readMotion(parsed['motion']),
    uiScale: parsed['uiScale'] === 'large' ? 'large' : 'normal',
    sessionReminder:
      typeof parsed['sessionReminder'] === 'boolean'
        ? parsed['sessionReminder']
        : DEFAULT_SETTINGS.sessionReminder,
  };
}

let current: Settings = load();
const listeners = new Set<(settings: Settings) => void>();

export function getSettings(): Settings {
  return current;
}

export function onSettingsChange(listener: (settings: Settings) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Repercute les preferences sur le DOM et sur l'audio. */
export function applySettings(): void {
  const root = document.documentElement;
  root.dataset['scale'] = current.uiScale;
  setReducedMotionMode(current.motion);
  syncMotionAttribute();
  audio.applySettings({
    musicVolume: current.musicVolume,
    sfxVolume: current.sfxVolume,
    muted: current.muted,
  });
}

export function updateSettings(patch: Partial<Settings>): Settings {
  current = { ...current, ...patch };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    // Persistance impossible : les reglages restent valables pour la session.
  }
  applySettings();
  for (const listener of [...listeners]) listener(current);
  return current;
}

export function toggleMute(): boolean {
  const next = !current.muted;
  updateSettings({ muted: next });
  if (!next) audio.play('click');
  return next;
}

// ---------------------------------------------------------------------------
// Panneau
// ---------------------------------------------------------------------------

function field(label: string, hint: string, control: HTMLElement): HTMLElement {
  return el('div', { class: 'field' }, [
    el('div', { class: 'field__label' }, [
      el('span', { class: 'field__name', text: label }),
      el('span', { class: 'field__hint', text: hint }),
    ]),
    control,
  ]);
}

function slider(
  value: number,
  ariaLabel: string,
  onInput: (value: number) => void,
): HTMLElement {
  const readout = el('output', { class: 'slider__value', text: `${Math.round(value * 100)} %` });
  const input = el('input', {
    class: 'slider__input',
    attrs: {
      type: 'range',
      min: '0',
      max: '100',
      step: '5',
      value: String(Math.round(value * 100)),
      'aria-label': ariaLabel,
    },
    on: {
      input: (event: Event) => {
        const target = event.currentTarget;
        if (!(target instanceof HTMLInputElement)) return;
        const next = Number(target.value) / 100;
        readout.textContent = `${target.value} %`;
        onInput(next);
      },
    },
  });
  return el('div', { class: 'slider' }, [input, readout]);
}

function toggle(checked: boolean, ariaLabel: string, onChange: (value: boolean) => void): HTMLElement {
  const input = el('input', {
    class: 'switch__input',
    attrs: { type: 'checkbox', 'aria-label': ariaLabel, ...(checked ? { checked: 'checked' } : {}) },
    on: {
      change: (event: Event) => {
        const target = event.currentTarget;
        if (!(target instanceof HTMLInputElement)) return;
        onChange(target.checked);
        audio.play('click');
      },
    },
  });
  return el('label', { class: 'switch' }, [input, el('span', { class: 'switch__track' })]);
}

function segmented<T extends string>(
  options: readonly { readonly value: T; readonly label: string }[],
  selected: T,
  name: string,
  onChange: (value: T) => void,
): HTMLElement {
  const group = el('div', { class: 'segmented', attrs: { role: 'radiogroup', 'aria-label': name } });
  for (const option of options) {
    const button = el('button', {
      class: 'segmented__option',
      text: option.label,
      attrs: {
        type: 'button',
        role: 'radio',
        'aria-checked': option.value === selected ? 'true' : 'false',
      },
      on: {
        click: () => {
          for (const child of group.children) child.setAttribute('aria-checked', 'false');
          button.setAttribute('aria-checked', 'true');
          onChange(option.value);
          audio.play('click');
        },
      },
    });
    group.appendChild(button);
  }
  return group;
}

let openSheet: Sheet | null = null;

export function openSettings(): void {
  if (openSheet) return;
  const sheet = createSheet({
    title: 'Paramètres',
    subtitle: 'Son, confort de lecture et rappels de session.',
    onClose: () => {
      openSheet = null;
    },
  });
  openSheet = sheet;

  const settings = getSettings();

  sheet.body.appendChild(
    el('div', { class: 'stack' }, [
      el('div', { class: 'group' }, [
        el('h3', { class: 'group__title', text: 'Son' }),
        field(
          'Musique',
          'Ambiance de fond du salon.',
          slider(settings.musicVolume, 'Volume de la musique', (value) =>
            updateSettings({ musicVolume: value }),
          ),
        ),
        field(
          'Effets',
          'Clics, jetons, transitions.',
          slider(settings.sfxVolume, 'Volume des effets', (value) => {
            updateSettings({ sfxVolume: value });
            audio.play('coin');
          }),
        ),
        field(
          'Couper le son',
          'Coupe tout, musique comprise.',
          toggle(settings.muted, 'Couper le son', (value) => updateSettings({ muted: value })),
        ),
      ]),

      el('div', { class: 'group' }, [
        el('h3', { class: 'group__title', text: 'Affichage' }),
        field(
          'Animations réduites',
          'Par défaut, on suit le réglage du système.',
          segmented(
            [
              { value: 'auto', label: 'Auto' },
              { value: 'on', label: 'Réduites' },
              { value: 'off', label: 'Complètes' },
            ] as const,
            settings.motion,
            'Animations',
            (value) => updateSettings({ motion: value }),
          ),
        ),
        field(
          "Taille de l'interface",
          'Agrandit textes et cibles tactiles.',
          segmented(
            [
              { value: 'normal', label: 'Normale' },
              { value: 'large', label: 'Grande' },
            ] as const,
            settings.uiScale,
            "Taille de l'interface",
            (value) => updateSettings({ uiScale: value }),
          ),
        ),
      ]),

      el('div', { class: 'group' }, [
        el('h3', { class: 'group__title', text: 'Session' }),
        field(
          'Rappel de pause',
          'Une invitation à souffler après 30 minutes de jeu continu.',
          toggle(settings.sessionReminder, 'Rappel de pause', (value) =>
            updateSettings({ sessionReminder: value }),
          ),
        ),
        el('p', { class: 'note' }, [
          'Le compteur de session reste visible en permanence dans le bandeau joueur, ',
          "que le rappel soit actif ou non.",
        ]),
      ]),

      el('p', { class: 'legal' }, [
        'Salon Minuit se joue exclusivement en jetons virtuels. ',
        "Aucun achat, aucun gain convertible, aucune valeur marchande.",
      ]),
    ]),
  );
}
